import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import distributorService from '../services/distributorService';
import { validCategorySettings } from '../services/categoryPricing';
import { useT } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';
import DistributorDashboard from '../components/DistributorDashboard';
import DistributorClinicSettings, {
  fromDetail,
  changed,
  matches,
} from '../components/distributor/DistributorClinicSettings';
import { DistributorActivity } from '../components/distributor/DistributorActivity';
import CashBankSection from '../components/distributor-cash/CashBankSection';
import DistributorPreviewNotice from '../components/distributor/DistributorPreviewNotice';
import DistributorDemoNotice from '../components/distributor/DistributorDemoNotice';
import DistributorOrders from '../components/distributor/DistributorOrders';
import DistributorIvrs from '../components/distributor/DistributorIvrs';
import DistributorCustomerTable from '../components/distributor/DistributorCustomerTable';
import NewDistributorCustomer from '../components/distributor/NewDistributorCustomer';
import DistributorCustomerDialog from '../components/distributor/DistributorCustomerDialog';
import { captureSession, isSessionCurrent } from '../utils/authSession';
import { IVR_TAB_STATUS_KEY } from '../features/ivr/ivrPresentation';
import '../styles/distributor-workspace.css';

const LOAD_ERROR = 'Could not load your clinics. Please try again.';
const ADDRESS = { street: '', city: '', state: '', zipCode: '' };
const addressKeys = Object.keys(ADDRESS);
const IVR_FILTER_STATUSES = new Set(Object.values(IVR_TAB_STATUS_KEY));
const PAGES = {
  overview: ['Overview', 'Your customers, orders, and product bank at a glance.'],
  customers: ['Customers', 'Manage your distributor customers and their account settings.'],
  orders: ['Orders', 'Create orders and follow your distributor order history.'],
  ivrs: ['IVRs', 'Submit and review coverage requests for your distributor customers.'],
  shipments: ['Shipments', 'Follow tracking and delivery for your distributor orders.'],
  bank: ['Tissue bank', 'Review your balance, buying power, and transactions.'],
};

export default function DistributorPortal() {
  const t = useT();
  const { user } = useAuth();
  const readOnly = Boolean(user?.distributorPreviewExpiresAt);
  const { pathname } = useLocation();
  const [params, setParams] = useSearchParams();
  const requestedStatuses = params.getAll('status');
  const ivrStatus =
    requestedStatuses.length === 1 && IVR_FILTER_STATUSES.has(requestedStatuses[0])
      ? requestedStatuses[0]
      : '';
  const changeIvrStatus = (status) => {
    const next = new URLSearchParams(params);
    if (IVR_FILTER_STATUSES.has(status)) next.set('status', status);
    else next.delete('status');
    setParams(next);
  };
  const activeSection = pathname.split('/')[2] || 'overview';
  const activityPage = ['orders', 'ivrs', 'shipments'].includes(activeSection);
  const [pageTitle, pageDescription] = PAGES[activeSection] || PAGES.overview;
  const [clinics, setClinics] = useState([]);
  const [clinicSource, setClinicSource] = useState(null);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [createError, setCreateError] = useState('');
  const [reload, setReload] = useState(0);
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [usersBusy, setUsersBusy] = useState(false);
  const [newClinic, setNewClinic] = useState({ name: '', ...ADDRESS });
  const [products, setProducts] = useState(null);
  const [orderRefresh, setOrderRefresh] = useState(0);
  const [settingsRevisions, setSettingsRevisions] = useState({});
  const [clinicQuery, setClinicQuery] = useState('');
  const detailRequest = useRef(0);
  const heading = useRef(null);
  const creatingRequest = useRef(false);
  const savingRequest = useRef(false);
  const mounted = useRef(true);
  const unsaved = selected && draft && Object.keys(changed(fromDetail(selected), draft)).length > 0;

  useEffect(() => {
    heading.current?.focus();
  }, [activeSection]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      detailRequest.current += 1;
    };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    const session = captureSession();
    const current = () => !controller.signal.aborted && isSessionCurrent(session);
    setLoading(true);
    setError('');
    Promise.all([
      distributorService.listClinics({ signal: controller.signal }),
      distributorService.getClinicSource({ signal: controller.signal }),
    ])
      .then(([response, sourceResponse]) => {
        if (!current()) return;
        if (
          !Array.isArray(response?.data?.data) ||
          typeof sourceResponse?.data?.data?.followsClientAccount !== 'boolean'
        )
          throw new Error('Invalid clinic list');
        setClinics(response.data.data);
        setClinicSource(sourceResponse.data.data);
      })
      .catch(() => {
        if (current()) {
          setClinicSource(null);
          setError(LOAD_ERROR);
        }
      })
      .finally(() => {
        if (current()) setLoading(false);
      });
    return () => controller.abort();
  }, [reload]);

  const openClinic = async (id) => {
    if (savingRequest.current) {
      setError('Wait for the current settings save to finish.');
      return;
    }
    if (usersBusy || unsaved) {
      setError('Save or discard your current edits before opening another clinic.');
      return;
    }
    const request = ++detailRequest.current;
    const session = captureSession();
    const current = () =>
      mounted.current && request === detailRequest.current && isSessionCurrent(session);
    setError('');
    setNotice('');
    setSelected(null);
    setDraft(null);
    setProducts(null);
    try {
      const [response, options] = await Promise.all([
        distributorService.getClinic(id),
        distributorService.productOptions().catch(() => null),
      ]);
      const detail = response?.data?.data;
      if (
        !detail?._id ||
        !validCategorySettings(detail.settings) ||
        !Number.isInteger(detail.revision)
      )
        throw new Error('Invalid clinic');
      if (current()) {
        setProducts(Array.isArray(options?.data?.data) ? options.data.data : null);
        setSelected(detail);
        setDraft(fromDetail(detail));
        setSettingsRevisions((current) => ({ ...current, [detail._id]: detail.revision }));
      }
    } catch {
      if (current()) setError('Could not open this clinic. Please try again.');
    }
  };

  const openNewCustomer = () => {
    if (usersBusy || unsaved || savingRequest.current) {
      setError('Save or discard your current edits before creating another clinic.');
      return;
    }
    setCreateError('');
    setCreateOpen(true);
  };
  const closeCustomer = () => {
    if (savingRequest.current || usersBusy || unsaved) return;
    const nextFocus = heading.current;
    detailRequest.current += 1;
    setSelected(null);
    setDraft(null);
    setProducts(null);
    setError('');
    setNotice('');
    requestAnimationFrame(() => {
      const focused = document.activeElement;
      if (
        mounted.current &&
        nextFocus?.isConnected &&
        heading.current === nextFocus &&
        (!focused || focused === document.body || !focused.isConnected)
      )
        nextFocus.focus();
    });
  };
  const submitNewClinic = async (event) => {
    event.preventDefault();
    if (readOnly || !createOpen || creatingRequest.current) return;
    if (usersBusy || unsaved) {
      setCreateError('Save or discard your current edits before creating another clinic.');
      return;
    }
    const name = newClinic.name.trim();
    const destination = Object.fromEntries(addressKeys.map((key) => [key, newClinic[key].trim()]));
    const hasAddress = addressKeys.some((key) => destination[key]);
    if (
      name.length < 2 ||
      (hasAddress &&
        (!destination.street ||
          !destination.city ||
          !/^[A-Z]{2}$/.test(destination.state) ||
          !/^\d{5}(?:-\d{4})?$/.test(destination.zipCode)))
    ) {
      setCreateError(
        'Enter a clinic name and a complete delivery address, or leave the address blank.'
      );
      return;
    }
    creatingRequest.current = true;
    const session = captureSession();
    setCreating(true);
    setError('');
    setNotice('');
    setCreateError('');
    try {
      const response = await distributorService.createClinic({
        name,
        ...(hasAddress ? { deliveryAddress: destination } : {}),
      });
      if (!mounted.current || !isSessionCurrent(session)) return;
      if (!response?.data?.data?._id) throw new Error('Unconfirmed clinic creation');
      setNewClinic({ name: '', ...ADDRESS });
      setCreateOpen(false);
      setNotice('Clinic created.');
      setReload((n) => n + 1);
      await openClinic(response.data.data._id);
    } catch {
      if (!mounted.current || !isSessionCurrent(session)) return;
      // A timed-out POST can have committed. Refresh the roster, but never resubmit automatically.
      setCreateError(
        'Clinic creation could not be confirmed. Check the clinic list before trying again.'
      );
      setReload((n) => n + 1);
    } finally {
      creatingRequest.current = false;
      if (mounted.current && isSessionCurrent(session)) setCreating(false);
    }
  };

  const save = async (event) => {
    event.preventDefault();
    if (readOnly || usersBusy || !selected || !draft || savingRequest.current) return;
    const baseline = fromDetail(selected);
    const changes = changed(baseline, draft);
    if (!Object.keys(changes).length) return;
    if (
      ['ratePerUnit', 'creditLimit'].some(
        (key) =>
          changes[key] !== undefined &&
          changes[key] !== null &&
          (!Number.isFinite(changes[key]) || changes[key] < 0)
      )
    ) {
      setError('Enter valid nonnegative amounts.');
      return;
    }
    if ('categoryRates' in changes && changes.categoryRates === null) {
      setError('Enter a retail price per piece from $0 to $1,000,000 with at most two decimals.');
      return;
    }
    const address = changes.deliveryAddress;
    if (
      address &&
      (!address.street ||
        !address.city ||
        !/^[A-Z]{2}$/.test(address.state) ||
        !/^\d{5}(?:-\d{4})?$/.test(address.zipCode))
    ) {
      setError('Enter a complete delivery address or clear all its fields.');
      return;
    }
    savingRequest.current = true;
    const session = captureSession();
    const request = detailRequest.current;
    const current = () =>
      mounted.current && request === detailRequest.current && isSessionCurrent(session);
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const response = await distributorService.saveSettings(selected._id, {
        revision: selected.revision,
        changes,
      });
      if (!current()) return;
      const detail = response?.data?.data;
      if (
        !validCategorySettings(detail?.settings) ||
        !Number.isInteger(detail.revision) ||
        detail._id !== selected._id ||
        detail.revision !== selected.revision + 1 ||
        !matches(detail, changes)
      )
        throw new Error('Unconfirmed settings save');
      setSelected(detail);
      setDraft(fromDetail(detail));
      setSettingsRevisions((current) => ({ ...current, [detail._id]: detail.revision }));
      setClinics((current) =>
        current.map((clinic) =>
          clinic._id === detail._id ? { ...clinic, settings: detail.settings } : clinic
        )
      );
      setNotice('Settings saved.');
    } catch {
      if (!current()) return;
      // 409 and unknown response both require a fresh read. Keep the unsaved draft intact.
      try {
        const response = await distributorService.getClinic(selected._id);
        if (!current()) return;
        const detail = response?.data?.data;
        if (!validCategorySettings(detail?.settings) || !Number.isInteger(detail.revision))
          throw new Error('Invalid clinic');
        setSelected(detail);
        setSettingsRevisions((current) => ({ ...current, [detail._id]: detail.revision }));
        setClinics((current) =>
          current.map((clinic) =>
            clinic._id === detail._id ? { ...clinic, settings: detail.settings } : clinic
          )
        );
        if (matches(detail, changes)) {
          setDraft(fromDetail(detail));
          setNotice('Settings saved.');
        } else
          setError(
            'Settings changed or could not be confirmed. Review the latest saved values before trying again. Your edits are still here.'
          );
      } catch {
        if (!current()) return;
        setError(
          'Settings could not be confirmed. Your edits are still here. Reload the clinic before trying again.'
        );
      }
    } finally {
      savingRequest.current = false;
      if (current()) setSaving(false);
    }
  };

  const setField = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const setAddress = (key, value) =>
    setDraft((current) => ({
      ...current,
      deliveryAddress: { ...current.deliveryAddress, [key]: value },
    }));
  const creatingDraft = Object.values(newClinic).some((value) => value.trim());
  useEffect(() => {
    const warn = (event) => {
      if (unsaved || creatingDraft || creating || saving) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [unsaved, creatingDraft, creating, saving]);
  const inputClass = 'me-dw-field block w-full rounded-lg border px-3 py-2';
  const visibleClinics = clinics.filter((clinic) =>
    String(clinic.name || '')
      .toLocaleLowerCase()
      .includes(clinicQuery.trim().toLocaleLowerCase())
  );

  return (
    <div
      className={`me-dw space-y-5 pb-6 ${activityPage ? 'me-dw-activity-page' : activeSection === 'bank' ? 'me-dw-bank-page mx-auto' : 'mx-auto max-w-7xl'}`}
    >
      {!activityPage && (
        <header
          className={`me-dw-header rounded-2xl ${activeSection === 'bank' ? 'me-dw-bank-page-header' : activeSection === 'overview' ? 'me-overview-page-header' : ''}`}
        >
          <div className="relative z-10 flex flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-5">
            <div className="min-w-0">
              {!['bank', 'overview'].includes(activeSection) && (
                <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[#0b7378]">
                  {t('Distributor workspace')}
                </p>
              )}
              <h1
                ref={heading}
                tabIndex={-1}
                className="text-2xl font-bold tracking-tight text-[#153d48]"
              >
                {t(pageTitle)}
              </h1>
              {!['bank', 'overview'].includes(activeSection) && (
                <p className="mt-1 text-sm text-[#5e737a]">{t(pageDescription)}</p>
              )}
            </div>
            {activeSection === 'customers' && (
              <div className="flex flex-wrap items-center justify-end gap-3">
                {!loading && !error && (
                  <span className="me-dw-status border border-[#cce7e2] px-3 py-1.5">
                    {t(clinics.length === 1 ? '{count} customer' : '{count} customers', {
                      count: clinics.length,
                    })}
                  </span>
                )}
                {!readOnly && clinicSource?.followsClientAccount === false && (
                  <button
                    type="button"
                    className="me-dw-primary"
                    onClick={openNewCustomer}
                    disabled={loading || creating}
                  >
                    <span aria-hidden="true">+ </span>
                    {t('New Customer')}
                  </button>
                )}
              </div>
            )}
          </div>
        </header>
      )}
      {readOnly && <DistributorPreviewNotice />}
      <DistributorDemoNotice user={user} />
      {loading && <p role="status">{t('Loading clinics...')}</p>}
      {error && !selected && (error === LOAD_ERROR || activeSection === 'customers') && (
        <div role="alert" className="text-red-700">
          {t(error)}{' '}
          {error === LOAD_ERROR && (
            <button type="button" onClick={() => setReload((n) => n + 1)} className="underline">
              {t('Retry')}
            </button>
          )}
        </div>
      )}
      {notice && !selected && activeSection === 'customers' && (
        <p role="status" className="text-green-700">
          {t(notice)}
        </p>
      )}
      <div
        id="distributor-overview"
        role="region"
        aria-label={t('Distributor overview page')}
        hidden={activeSection !== 'overview'}
        className="me-dw-section"
      >
        {activeSection === 'overview' && (
          <DistributorDashboard t={t} refresh={orderRefresh + reload} />
        )}
      </div>
      <CashBankSection
        active={activeSection === 'bank'}
        refresh={orderRefresh}
        {...{ t, readOnly }}
      />
      <section hidden={activeSection !== 'orders'} className="me-dw-section">
        <DistributorOrders
          headingRef={heading}
          clinics={clinics}
          loading={loading}
          settingsRevisions={settingsRevisions}
          refresh={orderRefresh}
          onSubmitted={() => setOrderRefresh((value) => value + 1)}
          active={activeSection === 'orders'}
          readOnly={readOnly}
          t={t}
        />
      </section>
      <section hidden={activeSection !== 'ivrs'} className="me-dw-section">
        <DistributorIvrs
          headingRef={heading}
          statusFilter={ivrStatus}
          onStatusFilterChange={changeIvrStatus}
          {...{ clinics, loading, readOnly, t }}
          active={activeSection === 'ivrs'}
        />
      </section>
      {activeSection === 'shipments' && (
        <DistributorActivity
          key="shipments"
          activityKind="shipments"
          pageHeader={{ headingRef: heading }}
          t={t}
        />
      )}
      <div
        id="distributor-customers"
        role="region"
        aria-label={t('Customer workspace')}
        hidden={activeSection !== 'customers'}
        className="me-dw-section space-y-4"
      >
        {clinicSource?.followsClientAccount ? (
          <p className="rounded-xl border border-[#cfe7e5] bg-[#ebf6f4] p-4 text-sm text-[#2d4e56]">
            {t(
              'Your clinics follow your MedEffects client account. Newly assigned clinics appear with ordering disabled until their settings are reviewed.'
            )}
          </p>
        ) : (
          clinicSource &&
          !readOnly &&
          createOpen &&
          activeSection === 'customers' && (
            <NewDistributorCustomer
              customer={newClinic}
              onChange={(key, value) => setNewClinic((current) => ({ ...current, [key]: value }))}
              onSubmit={submitNewClinic}
              onClose={() => {
                setCreateOpen(false);
                setNewClinic({ name: '', ...ADDRESS });
                setCreateError('');
              }}
              dirty={creatingDraft}
              error={createError}
              {...{ creating, inputClass, t }}
            />
          )
        )}
        {!loading && clinics.length > 0 && (
          <label className="block max-w-sm text-sm">
            {t('Search customers')}
            <input
              type="search"
              className="me-dw-field mt-1 block w-full rounded-lg border px-3 py-2"
              value={clinicQuery}
              onChange={(event) => setClinicQuery(event.target.value)}
            />
          </label>
        )}
        <div className="space-y-5">
          {!loading && !error && clinics.length === 0 && (
            <p className="me-dw-card p-6 text-sm text-slate-600">
              {t(
                readOnly
                  ? 'No distributor customers yet.'
                  : clinicSource?.followsClientAccount === false
                    ? 'No distributor customers yet. Select New Customer to get started.'
                    : 'No clinics are associated with your distributor account yet.'
              )}
            </p>
          )}
          {!loading && clinics.length > 0 && visibleClinics.length === 0 && (
            <p role="status" className="me-dw-card p-5 text-sm text-slate-600">
              {t('No customers match your search.')}
            </p>
          )}
          {!loading && clinics.length > 0 && visibleClinics.length > 0 && (
            <DistributorCustomerTable
              key={clinicQuery}
              clinics={visibleClinics}
              selectedId={selected?._id}
              onView={openClinic}
              t={t}
            />
          )}
          {selected && (
            <DistributorCustomerDialog
              customer={selected}
              open={activeSection === 'customers'}
              locked={Boolean(saving || usersBusy || unsaved)}
              onClose={closeCustomer}
              error={error}
              notice={activeSection === 'customers' ? notice : ''}
              onRetry={error === LOAD_ERROR ? () => setReload((value) => value + 1) : undefined}
              t={t}
            >
              {draft && (
                <DistributorClinicSettings
                  workspace
                  accountChangesPending={usersBusy}
                  isSavingSettings={saving}
                  selected={selected}
                  draft={draft}
                  products={products}
                  saving={saving || usersBusy}
                  usersReadOnly={saving || creating}
                  onUsersBusyChange={setUsersBusy}
                  readOnly={readOnly}
                  unsaved={unsaved}
                  inputClass={inputClass}
                  setField={setField}
                  setAddress={setAddress}
                  onSave={save}
                  onDiscard={() => {
                    setDraft(fromDetail(selected));
                    setError('');
                  }}
                  t={t}
                />
              )}
            </DistributorCustomerDialog>
          )}
        </div>
      </div>
    </div>
  );
}
